import { selector } from "recoil"

import { pluginStudioAtom } from "../atoms/pluginStudioAtom"
import { getCodeEditorPluginId, PluginStudioState, UserRole } from "../types"

/**
 * Selector for the entire plugin studio state
 * This is a bi-directional selector that allows for both reading and writing the state
 */
export const pluginStudioStateSelector = selector<PluginStudioState>({
  key: "pluginStudio.state",
  get: ({ get }) => {
    return get(pluginStudioAtom)
  },
  set: ({ set }, newValue) => {
    set(pluginStudioAtom, newValue)
  },
})

/**
 * Selector for the active plugin based on codeEditorPluginId
 * Returns the current plugin being edited in the studio
 */
export const activePluginSelector = selector({
  key: "pluginStudio.activePlugin",
  get: ({ get }) => {
    const state = get(pluginStudioAtom)
    const activePluginId = getCodeEditorPluginId(state)

    console.log(
      `[d2-selector] activePluginSelector called with ID: ${activePluginId}`,
    )

    if (!activePluginId) return null

    const plugin = state.plugins.find((plugin) => plugin.id === activePluginId)

    console.log(
      `[d2-selector] activePluginSelector found plugin: ${plugin ? plugin.id : "null"}`,
    )

    return plugin || null
  },
})

/**
 * Selector for the active file being edited in the current plugin
 * This is a bi-directional selector that allows both reading and updating the file content
 */
export const activeFileSelector = selector({
  key: "pluginStudio.activeFile",
  get: ({ get }) => {
    const state = get(pluginStudioAtom)
    const activePlugin = get(activePluginSelector)

    console.log(
      `[d2-selector] activeFileSelector called with plugin: ${activePlugin?.id || "null"}`,
    )

    if (!activePlugin) return null

    const pluginState = state.pluginStates[activePlugin.id]
    if (!pluginState || !pluginState.activeFileId) return null

    const file = activePlugin.files.find(
      (file) => file.id === pluginState.activeFileId,
    )

    console.log(
      `[d2-selector] activeFileSelector found file: ${file?.id || "null"}, type: ${file?.type || "null"}`,
    )

    return file || null
  },
  set: ({ get, set }, newValue) => {
    const state = get(pluginStudioAtom)
    const activePlugin = get(activePluginSelector)

    if (!activePlugin) return

    const pluginState = state.pluginStates[activePlugin.id]
    if (!pluginState || !pluginState.activeFileId) return

    const fileId = pluginState.activeFileId
    const file = activePlugin.files.find((file) => file.id === fileId)
    if (!file) return

    // Handle string value as new code content
    if (typeof newValue === "string") {
      // Create updated plugin with new code content using proper immutable updates
      const updatedPlugin = {
        ...activePlugin,
        code: {
          ...activePlugin.code,
          // Update the appropriate code property based on file type
          ...(file.type === UserRole.GUEST && {
            frontendGuestCodeWeb: newValue,
          }),
          ...(file.type === UserRole.HOST_EVENT_VIEW && {
            frontendHostViewCodeWeb: newValue,
          }),
          ...(file.type === UserRole.HOST_CONFIG_VIEW && {
            frontendHostConfigCodeWeb: newValue,
          }),
        },
        files: activePlugin.files.map((f) =>
          f.id === fileId
            ? { ...f, content: newValue, lastModified: Date.now() }
            : f,
        ),
      }

      // Update state with new plugin
      set(pluginStudioAtom, {
        ...state,
        plugins: state.plugins.map((p) =>
          p.id === activePlugin.id ? updatedPlugin : p,
        ),
        isPreviewStale: true,
      })
    }
    // Handle object with transpiledContent property
    else if (
      newValue &&
      typeof newValue === "object" &&
      "transpiledContent" in newValue
    ) {
      const transpiledCode = newValue.transpiledContent

      // Create updated plugin with new transpiled content
      const updatedPlugin = {
        ...activePlugin,
        code: {
          ...activePlugin.code,
          // Update the appropriate transpiled code property based on file type
          ...(file.type === UserRole.GUEST && {
            transpiledFrontendGuestCodeWeb: transpiledCode,
          }),
          ...(file.type === UserRole.HOST_EVENT_VIEW && {
            transpiledFrontendHostViewCodeWeb: transpiledCode,
          }),
          ...(file.type === UserRole.HOST_CONFIG_VIEW && {
            transpiledFrontendHostConfigCodeWeb: transpiledCode,
          }),
        },
        files: activePlugin.files.map((f) =>
          f.id === fileId
            ? {
                ...f,
                transpiledContent: transpiledCode,
                lastModified: Date.now(),
              }
            : f,
        ),
      }

      // Update state with new plugin
      set(pluginStudioAtom, {
        ...state,
        plugins: state.plugins.map((p) =>
          p.id === activePlugin.id ? updatedPlugin : p,
        ),
        isPreviewStale: false, // Not stale since we just updated transpiled content
      })
    }
  },
})

/**
 * Selector for the active file state including open files
 */
export const activeFileStateSelector = selector({
  key: "pluginStudio.activeFileState",
  get: ({ get }) => {
    const state = get(pluginStudioAtom)
    const activePlugin = get(activePluginSelector)

    if (!activePlugin) return null

    return state.pluginStates[activePlugin.id] || null
  },
})

/**
 * Selector for active file metadata that excludes code content
 * This prevents unnecessary re-renders when code content changes
 */
export const activeFileMetadataSelector = selector({
  key: "pluginStudio.activeFileMetadata",
  get: ({ get }) => {
    const activeFile = get(activeFileSelector)

    if (!activeFile) return null

    // Return a version without code content
    const { content, transpiledContent, ...metadata } = activeFile
    return metadata
  },
})

/**
 * Selector for active file type and transpiled content only
 * Used by the preview panel to only re-render when these change
 */
export const activeFilePreviewSelector = selector({
  key: "pluginStudio.activeFilePreview",
  get: ({ get }) => {
    const activeFile = get(activeFileSelector)

    if (!activeFile) return null

    // Return only what's needed for preview
    return {
      type: activeFile.type,
      transpiledContent: activeFile.transpiledContent,
    }
  },
})

// Plugin selectors
export {
  pluginByIdSelector,
  activePluginEditorStateSelector,
  openFilesSelector,
} from "./pluginSelectors"

// Test data selectors
export {
  activeTestCaseSelector,
  activeLiveUserSelector,
  liveDataModifiedSelector,
} from "./testDataSelectors"

// UI state selectors
export { uiStateSelector } from "./uiStateSelectors"
